import {
  type Customer,
  type CustomerInput,
  CustomerStatuses,
  customerInputSchema,
  routes,
} from '@pmai/contracts';
import { FormDialog, SelectField, TextAreaField, TextField } from '@pmai/ui';
import { useTranslation } from 'react-i18next';
import { bind } from '../../shared/forms/form';
import { useEntityForm } from '../../shared/forms/use-entity-form';
import { useEnumLabel } from '../../shared/i18n';

const emptyValues: CustomerInput = {
  name: '',
  regNr: '',
  contactPerson: '',
  email: '',
  phone: '',
  address: '',
  website: '',
  contractUrl: '',
  status: '',
  notes: '',
};

const valuesOf = (c: Customer): CustomerInput => ({
  name: c.name,
  regNr: c.regNr ?? '',
  contactPerson: c.contactPerson ?? '',
  email: c.email ?? '',
  phone: c.phone ?? '',
  address: c.address ?? '',
  website: c.website ?? '',
  contractUrl: c.contractUrl ?? '',
  status: c.status ?? '',
  notes: c.notes ?? '',
});

/** Create (no `customer`) or edit a customer — FR-CUST-005/006. */
export function CustomerDialog({
  open,
  customer,
  onClose,
  onSaved,
}: {
  open: boolean;
  customer?: Customer | null;
  onClose: () => void;
  onSaved?: (customer: Customer) => void;
}) {
  const { t } = useTranslation();
  const statusLabel = useEnumLabel();
  const { form, create, update, dialog } = useEntityForm({
    schema: customerInputSchema,
    open,
    record: customer ?? null,
    values: customer ? valuesOf(customer) : emptyValues,
    create: routes.customers.create,
    update: routes.customers.update,
    invalidates: [routes.customers.list, routes.customers.get],
    onClose,
    onSaved,
  });

  return (
    <FormDialog
      {...dialog}
      size="lg"
      title={customer ? t('customers.editTitle') : t('customers.new')}
      onSubmit={form.handleSubmit((body) =>
        customer
          ? update.mutate({ params: { id: customer.id }, body: { ...body, version: customer.version } })
          : create.mutate({ body }),
      )}
    >
      <TextField label={t('customers.fields.name')} required {...bind(form, 'name')} />
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField label={t('customers.fields.regNr')} {...bind(form, 'regNr')} />
        <SelectField
          label={t('customers.fields.status')}
          placeholder={t('common.notSet')}
          options={CustomerStatuses.options.map((o) => ({ value: o.code, label: statusLabel(o) }))}
          {...bind(form, 'status')}
        />
        <TextField label={t('customers.fields.contactPerson')} {...bind(form, 'contactPerson')} />
        <TextField label={t('customers.fields.email')} type="email" {...bind(form, 'email')} />
        <TextField label={t('customers.fields.phone')} type="tel" {...bind(form, 'phone')} />
        <TextField label={t('customers.fields.address')} {...bind(form, 'address')} />
        <TextField
          label={t('customers.fields.website')}
          type="url"
          placeholder="https://"
          {...bind(form, 'website')}
        />
        <TextField
          label={t('customers.fields.contractUrl')}
          type="url"
          placeholder="https://"
          {...bind(form, 'contractUrl')}
        />
      </div>
      <TextAreaField label={t('customers.fields.notes')} {...bind(form, 'notes')} />
    </FormDialog>
  );
}
